// Sagas for respondent/candidate app

import { put, takeEvery, select } from 'redux-saga/effects'
import Api from 'utils/api'
import { startTimer } from 'ducks/timer'
import { errorAction } from 'ducks/error'
import {
  resetRespondent,
  updateRespondent,
  getRespondent,
  START_GMA,
  FINISH_GMA,
  FINISH_INTEREST,
  finishGMA,
  CHANGE_GMA_ANSWER,
  START_INTEREST,
  START_PERSONALITY,
  FINISH_PERSONALITY,
  CHANGE_PERSONALITY_ANSWER,
  CHANGE_INTEREST_ANSWER,
  CHANGE_LANGUAGE,
  REUSE_CONSENT,
} from 'ducks/respondent'
import {
  getLocation,
  QUIZ_MAIN,
  PERSONALITY_QUIZ,
  INTEREST_QUIZ,
  GMA_QUIZ,
  GMA_QUIZ_GUIDE,
  GMA_QUIZ_WELCOME,
  CANDIDATE_REPORT,
  EXTERNAL_CANDIDATE_REPORT,
  toQuiz,
  toGmaQuiz,
  toPersonalityQuiz,
  toInterestQuiz,
} from 'ducks/router'

export default function* respondentSaga() {
  yield takeEvery([START_PERSONALITY, START_INTEREST, START_GMA], redirectSaga)
  yield takeEvery(
    [
      CANDIDATE_REPORT,
      EXTERNAL_CANDIDATE_REPORT,
      PERSONALITY_QUIZ,
      INTEREST_QUIZ,
      GMA_QUIZ,
      GMA_QUIZ_GUIDE,
      GMA_QUIZ_WELCOME,
      QUIZ_MAIN,
    ],
    fetchRespondentSaga,
  )
  yield takeEvery(CHANGE_LANGUAGE, fetchInstrumentsSaga)
  yield takeEvery(CHANGE_LANGUAGE, fetchInterestQuestionsSaga)
  yield takeEvery(CHANGE_LANGUAGE, changeLanguageSaga)
  yield takeEvery(
    [
      START_PERSONALITY,
      CHANGE_PERSONALITY_ANSWER,
      START_GMA,
      START_INTEREST,
      CHANGE_INTEREST_ANSWER,
      CHANGE_GMA_ANSWER,
    ],
    patchRespondentSaga,
  )
  yield takeEvery(FINISH_GMA, finishGMASaga)
  yield takeEvery(FINISH_PERSONALITY, finishPersonalitySaga)
  yield takeEvery(REUSE_CONSENT, reuseConsentSaga)
  yield takeEvery(FINISH_INTEREST, finishInterestSaga)
}

function* fetchInstrumentsSaga(action) {
  const { personality, language } = yield select(getRespondent)
  if (personality.language !== language) {
    const { error, response } = yield Api.get('instruments', { language })
    if (error) yield put(errorAction(error))
    else yield put(updateRespondent(reshapeInstruments(response)))
  }
}

function* fetchInterestQuestionsSaga(action) {
  const { language } = yield select(getRespondent)

  const { error, response } = yield Api.get('interest-questions', {
    language,
  })
  if (error) yield put(errorAction(error))
  else yield put(updateRespondent(reshapeInterestQuestions(response)))
}

function* fetchRespondentSaga(action) {
  const { uuid } = action.payload
  const { id } = yield select(getRespondent)

  if (id !== uuid || action.type === QUIZ_MAIN) {
    // Fetch if the id has changed or user enters main page
    // Changing id should only really happen during QA testing, since a responent
    // would only ever visit their own tests
    // Entering main page requires fetching results pdf link
    yield put(resetRespondent())
    const { error, response, meta } = yield Api.get(['respondents', uuid])

    if (error) yield put(errorAction(error))
    else {
      yield put(updateRespondent(syncronizeTime(response, meta)))
      if (response.gma.status === 'started') yield put(startTimer())
    }
  }
  yield* fetchInstrumentsSaga(action)
  yield* fetchInterestQuestionsSaga(action)
}

const statusChanged = (prop, state, response) => {
  const s0 = state[prop].status,
    s1 = response[prop].status
  return s1 !== s0 ? s1 : null
}

function* patchRespondentSaga(action) {
  const respondent = yield select(getRespondent)
  const { response, error, meta } = yield Api.patch(
    ['respondents', respondent.id],
    {
      action,
    },
  )

  if (error) yield put(errorAction(error))
  else {
    const pc = statusChanged('personality', respondent, response)
    const gc = statusChanged('gma', respondent, response)
    const ic = statusChanged('interest', respondent, response)
    if (pc || gc || ic) {
      yield put(updateRespondent(syncronizeTime(response, meta)))
      if (gc === 'started') yield put(startTimer())
      if (gc === 'completed') yield put(finishGMA({ reason: 'patch' }))
    }
  }
}

function* redirectSaga(action) {
  const { payload: { uuid } = {} } = yield select(getLocation)

  if (action.type === START_GMA) {
    yield put(toGmaQuiz({ uuid }))
    yield put(startTimer())
  }
  if (action.type === START_PERSONALITY) {
    yield put(toPersonalityQuiz({ uuid }))
  }
  if (action.type === START_INTEREST) {
    yield put(toInterestQuiz({ uuid }))
  }
}

function* finishGMASaga(action) {
  const { gma, id } = yield select(getRespondent)
  const payload = {
    answers: gma.answers,
    action: { ...action, timeDelta: gma.timeDelta },
  }
  const { response, error, meta } = yield Api.patch(
    ['respondents', id],
    payload,
  )
  if (error) yield put(errorAction(error))
  else yield put(updateRespondent(syncronizeTime(response, meta)))
}

function* finishPersonalitySaga(action) {
  const { personality, id } = yield select(getRespondent)
  const payload = { answers: personality.answers, action }
  const { response, error } = yield Api.patch(['respondents', id], payload)
  if (error) yield put(errorAction(error))
  else yield put(updateRespondent(response))
}

function* reuseConsentSaga(action) {
  const { id } = yield select(getRespondent)
  const { response, error, meta } = yield Api.patch(
    ['respondents', id, 'reuse-consent'],
    action.payload,
  )
  if (error) yield put(errorAction(error))
  else yield put(updateRespondent(syncronizeTime(response, meta)))
}

function* changeLanguageSaga(action) {
  const { id } = yield select(getRespondent)
  const { response, error, meta } = yield Api.patch(
    ['respondents', id, 'language'],
    action.payload,
  )
  if (error) yield put(errorAction(error))
  else yield put(updateRespondent(syncronizeTime(response, meta)))
}

function* finishInterestSaga(action) {
  const { interest, personality, id } = yield select(getRespondent)
  const payload = { answers: interest.answers, action }
  const { response, error } = yield Api.patch(['respondents', id], payload)
  if (error) yield put(errorAction(error))
  else yield put(updateRespondent(response))
  yield put(toQuiz({ uuid: id }))
}

const syncronizeTime = ({ gma, ...payload }, { serverTime, timeDelta }) => {
  // syncronize local time and server time
  const { started, completed, secondsTotal } = gma
  // new Date().valueOf() is epoch time in milliseconds
  const t0 = new Date(started || serverTime).valueOf()
  const t1 = new Date(completed || serverTime).valueOf()
  const secondsElapsed = Math.max(0, (t1 - t0) / 1000)
  const secondsLeft = Math.max(0, secondsTotal - secondsElapsed)
  return {
    ...payload,
    gma: {
      secondsLeft,
      secondsElapsed,
      timeDelta,
      ...gma,
    },
  }
}

const reshapeInstruments = ({ language, results: instruments }) => {
  const questions: { key: unknown; instrument: string }[] = []
  for (const instrument in instruments) {
    for (const { key } of instruments[instrument].questions) {
      questions.push({ key, instrument })
    }
  }
  return { personality: { language, instruments, questions } }
}

const reshapeInterestQuestions = instruments => {
  return {
    interest: {
      instruments,
      questions: instruments.questions,
      language: instruments.language,
    },
  }
}
